import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { EditOutlined, DeleteOutline } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { companyValueError } from "../../services/companyPromptValues";
import { AuthContext } from "../../context/Auth/AuthContext";

const types = [
  ["text", "Texto curto"],
  ["textarea", "Texto longo"],
  ["number", "Número"],
  ["boolean", "Sim ou não"],
  ["select", "Lista de opções"],
];
const empty = () => ({
  label: "",
  key: "",
  type: "text",
  value: "",
  description: "",
  optionsText: "",
});
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    borderRadius: 18,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "center",
    marginBottom: 20,
  },
  fields: { display: "flex", flexDirection: "column", gap: 20, paddingTop: 8 },
  value: {
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  table: { overflowX: "auto" },
}));

export default function CompanyPromptValues() {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [discard, setDiscard] = useState(false);
  const [usages, setUsages] = useState(null);
  const [usageError, setUsageError] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const usageRequest = useRef(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get("/prompt-values", { params: { search, page } })
        .then(({ data }) => {
          if (active) {
            setRows(data.variables);
            setMore(data.hasMore);
            setLoadError("");
          }
        })
        .catch((err) => {
          if (active) setLoadError(companyValueError(err));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, page, reload, user.companyId]);
  useEffect(() => {
    const event = `company-${user.companyId}-prompt-values`;
    const refresh = () => setReload((n) => n + 1);
    socket?.on(event, refresh);
    return () => socket?.off(event, refresh);
  }, [socket, user.companyId]);
  useEffect(() => {
    usageRequest.current += 1;
    setRows([]);
    setDraft(null);
    setPage(1);
    return () => {
      usageRequest.current += 1;
    };
  }, [user.companyId]);

  const open = async (row) => {
    const requestId = ++usageRequest.current;
    const next = row
      ? {
          ...row,
          description: row.description || "",
          optionsText: (row.options || []).join("\n"),
        }
      : empty();
    setDraft(next);
    setBaseline(JSON.stringify(next));
    setError("");
    setUsages(null);
    setUsageError(false);
    setDeleting(false);
    if (row) {
      try {
        const { data } = await api.get(`/prompt-values/${row.id}/usages`);
        if (usageRequest.current === requestId) setUsages(data.agents);
      } catch {
        if (usageRequest.current === requestId) setUsageError(true);
      }
    } else setUsages([]);
  };
  const close = () => {
    if (saving) return;
    if (JSON.stringify(draft) !== baseline) setDiscard(true);
    else setDraft(null);
  };
  const save = async () => {
    if (saving) return;
    if (
      !/^[a-z][a-z0-9_]{0,59}$/.test(draft.key) ||
      ["constructor", "prototype"].includes(draft.key)
    ) {
      setError(
        "Use uma chave com letras minúsculas, números e sublinhado, começando por uma letra (até 60 caracteres)."
      );
      return;
    }
    if (draft.label.trim().length < 2) {
      setError("Informe um nome com pelo menos 2 caracteres.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        label: draft.label,
        key: draft.key,
        type: draft.type,
        description: draft.description,
        value:
          draft.type === "number" && String(draft.value).trim()
            ? Number(draft.value)
            : draft.value,
        options:
          draft.type === "select"
            ? draft.optionsText
                .split("\n")
                .map((v) => v.trim())
                .filter(Boolean)
            : undefined,
      };
      if (draft.id) await api.put(`/prompt-values/${draft.id}`, payload);
      else await api.post("/prompt-values", payload);
      toast.success("Variável da empresa salva.");
      setDraft(null);
      setReload((n) => n + 1);
    } catch (err) {
      setError(companyValueError(err));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setError("");
    try {
      await api.delete(`/prompt-values/${draft.id}`);
      setDraft(null);
      setReload((n) => n + 1);
      toast.success("Variável excluída.");
    } catch (err) {
      setError(companyValueError(err));
      setDeleting(false);
    } finally {
      setSaving(false);
    }
  };
  const used = (usages?.length || 0) > 0;
  return (
    <Paper className={classes.root}>
      <div className={classes.toolbar}>
        <Box flex={1}>
          <Typography variant="h6">Variáveis da empresa</Typography>
          <Typography variant="body2" color="textSecondary">
            Informações reutilizáveis nos agentes desta empresa. Atualize uma
            vez para aplicar aos agentes vinculados.
          </Typography>
        </Box>
        <Button variant="contained" color="primary" onClick={() => open(null)}>
          Criar variável da empresa
        </Button>
      </div>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        id="company-value-search"
        label="Buscar por nome ou chave"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />
      {loadError && (
        <Alert
          severity="error"
          action={
            <Button onClick={() => setReload((n) => n + 1)}>
              Tentar novamente
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      <div className={classes.table}>
        <Table size="small" aria-label="Variáveis da empresa">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Chave</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.label}
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    {types.find(([type]) => type === row.type)?.[1]}
                  </Typography>
                </TableCell>
                <TableCell>{row.key}</TableCell>
                <TableCell>
                  <Typography variant="body2" className={classes.value}>
                    {String(row.value)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar variável e consultar agentes vinculados">
                    <IconButton
                      aria-label={`Editar ${row.label}`}
                      onClick={() => open(row)}
                    >
                      <EditOutlined />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {loading ? (
        <Box p={2} role="status">
          Carregando variáveis...
        </Box>
      ) : (
        !rows.length &&
        !loadError && (
          <Box p={3}>
            <Typography color="textSecondary">
              {search
                ? "Nenhuma variável encontrada."
                : "Cadastre, por exemplo, o horário de atendimento ou o telefone do suporte."}
            </Typography>
          </Box>
        )
      )}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          disabled={page === 1 || loading}
          onClick={() => setPage((n) => n - 1)}
        >
          Anterior
        </Button>
        <Button
          disabled={!more || loading}
          onClick={() => setPage((n) => n + 1)}
        >
          Próxima
        </Button>
      </Box>
      <Dialog
        open={Boolean(draft)}
        onClose={close}
        fullWidth
        maxWidth="sm"
        aria-labelledby="company-value-title"
      >
        <DialogTitle id="company-value-title">
          {draft?.id ? "Editar variável da empresa" : "Criar variável da empresa"}
        </DialogTitle>
        <DialogContent>
          {draft && (
            <div className={classes.fields}>
              <TextField
                autoFocus
                id="company-value-label"
                label="Nome"
                value={draft.label}
                disabled={saving}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
              <TextField
                id="company-value-key"
                label="Chave"
                value={draft.key}
                disabled={saving || Boolean(draft.id)}
                helperText="Identificador estável, como horario_atendimento."
                inputProps={{ maxLength: 60 }}
                onChange={(e) =>
                  setDraft({ ...draft, key: e.target.value.toLowerCase() })
                }
              />
              <TextField
                select
                id="company-value-type"
                label="Tipo"
                value={draft.type}
                disabled={saving || used || usages === null}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    type: e.target.value,
                    value: e.target.value === "boolean" ? false : "",
                  })
                }
              >
                {types.map(([type, label]) => (
                  <MenuItem key={type} value={type}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              {draft.type === "select" && (
                <TextField
                  multiline
                  minRows={2}
                  id="company-value-options"
                  label="Opções (uma por linha)"
                  value={draft.optionsText}
                  disabled={saving || used}
                  onChange={(e) =>
                    setDraft({ ...draft, optionsText: e.target.value })
                  }
                />
              )}
              {draft.type === "boolean" ? (
                <FormControlLabel
                  label="Valor: sim"
                  control={
                    <Checkbox
                      checked={Boolean(draft.value)}
                      disabled={saving}
                      onChange={(e) =>
                        setDraft({ ...draft, value: e.target.checked })
                      }
                    />
                  }
                />
              ) : (
                <TextField
                  id="company-value-value"
                  label="Valor compartilhado"
                  multiline={draft.type === "textarea"}
                  minRows={draft.type === "textarea" ? 4 : undefined}
                  maxRows={10}
                  type={draft.type === "number" ? "number" : "text"}
                  select={draft.type === "select"}
                  value={draft.value}
                  disabled={saving}
                  inputProps={{ maxLength: 5000 }}
                  onChange={(e) =>
                    setDraft({ ...draft, value: e.target.value })
                  }
                >
                  {draft.type === "select" &&
                    draft.optionsText
                      .split("\n")
                      .map((v) => v.trim())
                      .filter(Boolean)
                      .map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                </TextField>
              )}
              <TextField
                id="company-value-description"
                label="Descrição (opcional)"
                multiline
                minRows={2}
                inputProps={{ maxLength: 1000 }}
                value={draft.description}
                disabled={saving}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
              />
              {draft.id && (
                <Box>
                  <Typography variant="subtitle2">
                    Agentes vinculados {usages ? `· ${usages.length}` : ""}
                  </Typography>
                  {usageError ? (
                    <Alert severity="error">
                      Não foi possível consultar os vínculos. Feche e tente
                      novamente.
                    </Alert>
                  ) : usages === null ? (
                    <Typography>Consultando vínculos...</Typography>
                  ) : used ? (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        O novo valor será usado na próxima execução.
                        Substituições locais serão preservadas.
                      </Typography>
                      {usages.map((agent) => (
                        <Typography key={agent.id} variant="body2">
                          {agent.name}
                        </Typography>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum agente vinculado.
                    </Typography>
                  )}
                </Box>
              )}
              {deleting && (
                <Alert severity="warning">Excluir esta variável da empresa?</Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {draft?.id && (
            <Button
              startIcon={<DeleteOutline />}
              disabled={saving || used || usages === null}
              onClick={() => (deleting ? remove() : setDeleting(true))}
            >
              {deleting ? "Confirmar exclusão" : "Excluir"}
            </Button>
          )}
          <Button disabled={saving} onClick={close}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saving || deleting || usages === null}
            onClick={save}
          >
            {saving ? "Salvando..." : "Salvar variável"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={discard}
        onClose={() => setDiscard(false)}
        aria-labelledby="company-value-discard"
      >
        <DialogTitle id="company-value-discard">
          Descartar alterações?
        </DialogTitle>
        <DialogContent>
          As alterações nesta variável ainda não foram salvas.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscard(false)}>Continuar editando</Button>
          <Button
            color="primary"
            onClick={() => {
              setDiscard(false);
              setDraft(null);
            }}
          >
            Descartar e sair
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
